import type { ID } from "@/lib/catalog/types";
import { api } from "@/lib/apiClient";
import { refreshStoredAuthUser } from "@/lib/authUser";

export interface UserAccessRole {
  id: string;
  name: string;
}

export interface UserAccessDirectPermission {
  id: string;
  name: string;
  title: string | null;
}

export interface UserAccessRecord {
  id: string;
  name: string;
  phone: string | null;
  companyId: string | null;
  companyName: string | null;
  legacyRole: string | null;
  email: string;
  roles: UserAccessRole[];
  directPermissions: UserAccessDirectPermission[];
  rolesCount: number;
  directPermissionsCount: number;
  createdAt: string;
  updatedAt: string;
}

export type UserAccessDraft = {
  roleNames: string[];
  permissionNames: string[];
  companyId: string | null;
};

export type CreateUserPayload = {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  password: string;
  accountRole: "super-admin" | "admin" | "employee" | "company-owner";
  roleNames: string[];
  permissionNames: string[];
  companyId: string | null;
};

const LS_KEY = "inventory.userAccess.v1";

type UserApiRecord = {
  id?: number | string;
  name: string;
  phone?: string | null;
  company_id?: number | null;
  company?: { id?: number; name?: string } | null;
  legacy_role?: string | null;
  email: string;
  roles?: { id?: number | string; name: string }[];
  direct_permissions?: { id?: number | string; name: string; title?: string | null }[];
  roles_count?: number;
  direct_permissions_count?: number;
  created_at?: string | null;
  updated_at?: string | null;
};

type UpdateApiResponse = {
  message?: string;
  data?: UserApiRecord;
};

type CreateApiResponse = UpdateApiResponse;

function dedupeById(items: UserAccessRecord[]): UserAccessRecord[] {
  const byId = new Map<ID, UserAccessRecord>();
  for (const item of items) byId.set(item.id, item);
  return Array.from(byId.values());
}

function load(): UserAccessRecord[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as UserAccessRecord[];
    return Array.isArray(parsed) ? dedupeById(parsed) : [];
  } catch {
    return [];
  }
}

function save(items: UserAccessRecord[]) {
  localStorage.setItem(LS_KEY, JSON.stringify(dedupeById(items)));
}

function extractRowsFromResponse(body: unknown): UserApiRecord[] {
  if (Array.isArray(body)) return body as UserApiRecord[];
  if (!body || typeof body !== "object") return [];
  const root = body as Record<string, unknown>;
  const data = root.data;
  if (Array.isArray(data)) return data as UserApiRecord[];
  if (data && typeof data === "object" && !Array.isArray(data)) {
    const inner = (data as Record<string, unknown>).data;
    if (Array.isArray(inner)) return inner as UserApiRecord[];
  }
  return [];
}

function readLastPageFromListBody(body: unknown): number | null {
  if (!body || typeof body !== "object") return null;
  const meta = (body as Record<string, unknown>).meta;
  if (meta && typeof meta === "object" && !Array.isArray(meta)) {
    const lp = (meta as Record<string, unknown>).last_page;
    if (typeof lp === "number" && lp >= 1) return lp;
  }
  return null;
}

function mapUserApiRecord(row: UserApiRecord): UserAccessRecord {
  const created = row.created_at || new Date().toISOString();
  const updated = row.updated_at || created;
  const roles = (row.roles ?? []).map((r) => ({
    id: String(r.id ?? ""),
    name: r.name,
  }));
  const directPermissions = (row.direct_permissions ?? []).map((p) => ({
    id: String(p.id ?? ""),
    name: p.name,
    title: p.title ?? null,
  }));
  return {
    id: String(row.id ?? ""),
    name: row.name,
    phone: row.phone ?? null,
    companyId: row.company_id != null ? String(row.company_id) : null,
    companyName: row.company?.name ? String(row.company.name) : null,
    legacyRole: row.legacy_role ?? null,
    email: row.email,
    roles,
    directPermissions,
    rolesCount: typeof row.roles_count === "number" ? row.roles_count : roles.length,
    directPermissionsCount: typeof row.direct_permissions_count === "number" ? row.direct_permissions_count : directPermissions.length,
    createdAt: created,
    updatedAt: updated,
  };
}

export function listUserAccess(): UserAccessRecord[] {
  return load().sort((a, b) => a.name.localeCompare(b.name));
}

const PER_PAGE = 50;

export async function syncUserAccessFromApi(search?: string) {
  const collected: UserApiRecord[] = [];
  let page = 1;
  let lastPage = 1;
  do {
    const response = await api.get("/api/v1/user-access/users", {
      params: { page, per_page: PER_PAGE, ...(search?.trim() ? { search: search.trim() } : {}) },
    });
    const body = response.data;
    collected.push(...extractRowsFromResponse(body));
    lastPage = readLastPageFromListBody(body) ?? 1;
    page += 1;
  } while (page <= lastPage && page <= 80);

  const records = dedupeById(collected.map(mapUserApiRecord).filter((r) => Boolean(r.id)));
  save(records);
  return listUserAccess();
}

export async function fetchUserAccessDetail(id: ID): Promise<UserAccessRecord> {
  const response = await api.get<{ data?: UserApiRecord }>(`/api/v1/user-access/users/${id}`);
  const raw = response.data?.data;
  if (!raw || raw.id == null) throw new Error("Invalid user access response.");
  return mapUserApiRecord(raw);
}

export async function updateUserAccess(id: ID, draft: UserAccessDraft): Promise<{ record: UserAccessRecord; message: string }> {
  const payload: Record<string, unknown> = {
    guard_name: "web",
    role_names: draft.roleNames,
    permission_names: draft.permissionNames,
    company_id: draft.companyId,
  };
  const response = await api.put<UpdateApiResponse>(`/api/v1/user-access/users/${id}`, payload);
  const raw = response.data?.data;
  if (!raw || raw.id == null) throw new Error("Invalid update user access response.");
  const record = mapUserApiRecord(raw);

  const items = load();
  const idx = items.findIndex((d) => d.id === record.id);
  if (idx >= 0) items[idx] = record;
  else items.unshift(record);
  save(items);

  // If the current user's own access was changed, update cached auth_user immediately.
  void refreshStoredAuthUser();

  return { record, message: response.data?.message || "User access updated successfully." };
}

export async function createUserAccess(payload: CreateUserPayload): Promise<{
  record: UserAccessRecord;
  message: string;
}> {
  const response = await api.post<CreateApiResponse>("/api/v1/user-access/users", {
    first_name: payload.firstName.trim(),
    last_name: payload.lastName.trim(),
    phone: payload.phone.trim() || null,
    email: payload.email.trim().toLowerCase(),
    password: payload.password,
    account_role: payload.accountRole,
    guard_name: "web",
    role_names: payload.roleNames,
    permission_names: payload.permissionNames,
    company_id: payload.companyId != null && payload.companyId !== "" ? Number(payload.companyId) : null,
  });
  const raw = response.data?.data;
  if (!raw || raw.id == null) throw new Error("Invalid create user response.");
  const record = mapUserApiRecord(raw);

  const items = load();
  items.unshift(record);
  save(items);

  void refreshStoredAuthUser();

  return {
    record,
    message: response.data?.message || "User created successfully.",
  };
}
